import { useMutation } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useData } from "../../runtime/provider";
import { NoAuthError } from "@utils/NoAuthError";
import { userMeWireSchema } from "@libs/wire";
import { USER_ME_QUERY_KEY, UserMeQueryData } from "./useMe";
import { meQuery } from "./useMe/queryFunctions";

export const useCompleteSetup = () => {
  const data = useData();
  const { queryClient, auth } = data;
  const serverCompletedRef = useRef(false);
  const mutation = useMutation({
    mutationFn: (input?: { method?: "genre" | "import" }) => {
      if (!auth.currentUser()) {
        throw new NoAuthError("useCompleteSetup");
      }
      return data.api.user.onboarding.completeSetup.mutate(
        input ?? { method: "genre" },
      );
    },
    onMutate: (input) => {
      const step =
        input?.method === "import"
          ? ("welcome_import_pending" as const)
          : ("welcome_pending" as const);
      data.cache.setQueryDataDurable<UserMeQueryData>(
        queryClient,
        USER_ME_QUERY_KEY,
        (old) => {
          const parsed = userMeWireSchema.safeParse(old);
          if (!parsed.success) return old;
          return { ...parsed.data, onboardingStep: step, onboarded: true };
        },
      );
    },
  });

  const serverMutateAsync = mutation.mutateAsync;
  const mutateAsync = useCallback(
    async (input?: { method?: "genre" | "import" }) => {
      if (!serverCompletedRef.current) {
        await serverMutateAsync(input);
        serverCompletedRef.current = true;
      }

      const cachedUser = queryClient.getQueryData(USER_ME_QUERY_KEY);
      if (userMeWireSchema.safeParse(cachedUser).success) return;

      // Setup is committed on the server at this point. Force an authoritative,
      // schema-validated profile fetch before callers navigate Home. If it fails,
      // a retry of mutateAsync skips the non-idempotent server transition and
      // retries only this safe hydration step.
      await queryClient.fetchQuery({
        ...meQuery(data),
        staleTime: 0,
        retry: 3,
        retryDelay: 1000,
      });
    },
    [serverMutateAsync, data, queryClient],
  );

  return { ...mutation, mutateAsync };
};

export const useCompleteWelcome = () => {
  const data = useData();
  const { queryClient, auth } = data;
  return useMutation({
    mutationFn: () => {
      if (!auth.currentUser()) {
        throw new NoAuthError("useCompleteWelcome");
      }
      return data.api.user.onboarding.completeWelcome.mutate();
    },
    onMutate: () => {
      data.cache.setQueryDataDurable<UserMeQueryData>(
        queryClient,
        USER_ME_QUERY_KEY,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            onboardingStep: "completed" as const,
            onboarded: true,
          };
        },
      );
    },
  });
};
